import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { CategoryTag, SkillLevelTag } from '@/types';
import { categoryService } from '@/services/categoryService';
import { useAuth } from './AuthContext';

interface CategoryContextType {
  categories: Map<string, CategoryTag>;
  skillLevels: Map<string, SkillLevelTag>;
  loading: boolean;
  error: string | null;
  refreshCategories: () => Promise<void>;
}

const CategoryContext = createContext<CategoryContextType>({
  categories: new Map(),
  skillLevels: new Map(),
  loading: false,
  error: null,
  refreshCategories: async () => {},
});

export const useCategoryContext = () => {
  const context = useContext(CategoryContext);
  if (!context) {
    throw new Error('useCategoryContext must be used within a CategoryProvider');
  }
  return context;
};

interface CategoryProviderProps {
  children: ReactNode;
}

export const CategoryProvider: React.FC<CategoryProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [categories, setCategories] = useState<Map<string, CategoryTag>>(new Map());
  const [skillLevels, setSkillLevels] = useState<Map<string, SkillLevelTag>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch both categories and skill levels in parallel
      const [fetchedCategories, fetchedSkillLevels] = await Promise.all([
        categoryService.getCategories(),
        categoryService.getSkillLevels()
      ]);

      // Convert arrays to Maps for O(1) lookup
      const categoriesMap = new Map(fetchedCategories.map(cat => [cat.id, cat]));
      const skillLevelsMap = new Map(fetchedSkillLevels.map(level => [level.id, level]));

      setCategories(categoriesMap);
      setSkillLevels(skillLevelsMap);
    } catch (err) {
      console.error('Failed to load categories:', err);
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const refreshCategories = async () => {
    await loadCategories();
  };

  useEffect(() => {
    // Only load categories when user is authenticated
    if (user) {
      loadCategories();
    } else {
      // Clear categories when user logs out
      setCategories(new Map());
      setSkillLevels(new Map());
      setLoading(false);
      setError(null);
    }
  }, [user]);

  const value: CategoryContextType = {
    categories,
    skillLevels,
    loading,
    error,
    refreshCategories,
  };

  return (
    <CategoryContext.Provider value={value}>
      {children}
    </CategoryContext.Provider>
  );
};
